import { LocalNotifications } from "@capacitor/local-notifications";

const isNative = () => !!(window.Capacitor?.isNativePlatform?.());

/**
 * Request notification permissions.
 * Call once on app start.
 */
export async function requestNotificationPermission() {
  if (!isNative()) return false;
  try {
    const { display } = await LocalNotifications.requestPermissions();
    return display === "granted";
  } catch (e) {
    console.error("[Notifications] Permission error:", e);
    return false;
  }
}

/**
 * Send a test notification in 5 seconds.
 */
export async function sendTestNotification() {
  if (!isNative()) return;
  try {
    await LocalNotifications.requestPermissions();
    const testDate = new Date(Date.now() + 5000);
    await LocalNotifications.schedule({
      notifications: [{
        id: 99999,
        title: "🔔 ELDE Test Notification",
        body: "Notifications are working correctly!",
        schedule: { at: testDate },
        sound: "default",
        actionTypeId: "",
      }],
    });
    console.log("[Notifications] Test notification scheduled for", testDate);
  } catch (e) {
    console.error("[Notifications] Test error:", e);
    alert("Notification error: " + e.message);
  }
}

/**
 * Generate a unique notification ID from account ID + reminder index.
 */
function notificationId(accountId, index) {
  return (accountId % 10000) * 100 + index;
}

/**
 * Cancel all notifications for an account.
 */
export async function cancelAllNotificationsForAccount(accountId, count = 10) {
  if (!isNative()) return;
  try {
    const ids = Array.from({ length: count }, (_, i) => ({ id: notificationId(accountId, i) }));
    await LocalNotifications.cancel({ notifications: ids });
  } catch (e) {}
}

/**
 * Schedule all reminders for a single account.
 */
export async function scheduleAccountNotifications(account, reminderDays, isPaid) {
  if (!isNative()) return;

  await cancelAllNotificationsForAccount(account.id, (reminderDays?.length || 0) + 5);

  if (isPaid || !reminderDays?.length || !account.paymentDate) return;

  try {
    const today = new Date();
    let dueDate;

    if (typeof account.paymentDate === "string" && account.paymentDate.includes("-")) {
      dueDate = new Date(account.paymentDate + "T00:00:00");
    } else {
      const day = parseInt(account.paymentDate);
      dueDate = new Date(today.getFullYear(), today.getMonth(), day);
      if (dueDate < today) {
        dueDate = new Date(today.getFullYear(), today.getMonth() + 1, day);
      }
    }

    const notifications = [];

    reminderDays.forEach((daysBefore, index) => {
      const notifyDate = new Date(dueDate);
      notifyDate.setDate(notifyDate.getDate() - daysBefore);

      if (daysBefore === 0) {
        // Day of — notify at 9 AM, or if past 9 AM today, skip
        notifyDate.setHours(9, 0, 0, 0);
      } else {
        // Future days — notify at 9 AM on that day
        notifyDate.setHours(9, 0, 0, 0);
      }

      // Skip if in the past
      if (notifyDate <= today) {
        console.log(`[Notifications] Skipping past notification for ${account.name} on ${notifyDate}`);
        return;
      }

      const daysLabel = daysBefore === 0
        ? "today"
        : daysBefore === 1
        ? "tomorrow"
        : daysBefore % 7 === 0
        ? `in ${daysBefore / 7} week${daysBefore / 7 > 1 ? "s" : ""}`
        : `in ${daysBefore} days`;

      notifications.push({
        id: notificationId(account.id, index),
        title: `💳 ${account.name} payment due ${daysLabel}`,
        body: `Your ${account.name} minimum payment of $${account.minPayment.toFixed(2)} is due ${daysLabel}. Tap to log it.`,
        schedule: { at: notifyDate },
        sound: "default",
        actionTypeId: "",
        extra: { accountId: account.id },
      });
    });

    if (notifications.length > 0) {
      await LocalNotifications.schedule({ notifications });
      console.log(`[Notifications] Scheduled ${notifications.length} reminder(s) for ${account.name}`);
    }
  } catch (e) {
    console.error("[Notifications] Schedule error:", e);
  }
}

/**
 * Reschedule all notifications for all accounts.
 */
export async function rescheduleAllNotifications(accounts, notificationSettings, monthlyPayments) {
  if (!isNative()) return;
  for (const account of accounts) {
    const reminderDays = notificationSettings[account.id] || [];
    const isPaid = !!(monthlyPayments?.paid?.[account.id]);
    await scheduleAccountNotifications(account, reminderDays, isPaid);
  }
}
